/** @jsxImportSource @emotion/react */
import {
  createStyles,
  DateInput,
  Input,
  Modal,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { usePostponeCase } from 'api/useCasesApi';

interface Props {
  caseId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const PostponeCaseModal = ({ caseId, onClose, onSuccess }: Props) => {
  const styles = useStyles();
  const { palette } = useTheme();
  const [postponeDate, setPostponeDate] = useState<Date | null>(null);
  const [noteContent, setNoteContent] = useState('');

  const postponeMutation = usePostponeCase(caseId);

  const handlePostpone = () => {
    if (!postponeDate || !noteContent.trim()) return;
    postponeMutation.mutate(
      { postponedUntil: postponeDate, note: noteContent },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      }
    );
  };

  const isSubmitDisabled =
    !postponeDate || !noteContent.trim() || postponeMutation.isPending;

  return (
    <Modal
      title="Postpone Case"
      primaryButtonProps={{
        text: postponeMutation.isPending ? 'Postponing...' : 'Postpone',
        onClick: handlePostpone,
        disabled: isSubmitDisabled,
        loading: postponeMutation.isPending,
      }}
      secondaryButtonProps={{
        text: 'Cancel',
        onClick: onClose,
      }}
    >
      <div css={styles.container}>
        <div css={styles.field}>
          <Typography variant="bodySmallest" color={palette.grey.x500}>
            Postpone until
          </Typography>
          <DateInput
            type="default"
            value={postponeDate}
            onChange={(date: Date | null) => {
              if (date) {
                setPostponeDate(date);
              }
            }}
            placeholder="Select date"
          />
        </div>
        <div css={styles.field}>
          <Input
            type="textarea"
            label="Write your note here..."
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            rows={3}
          />
        </div>
        {postponeMutation.error && (
          <Typography variant="bodySmall" color={palette.danger.default}>
            {postponeMutation.error.errorMessage ?? 'Failed to postpone case'}
          </Typography>
        )}
      </div>
    </Modal>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxxs,
  },
}));
